<template>
  <div class="container mx-auto px-4 py-8 max-w-4xl">
    <!-- 헤더 -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900">새 게시글 작성</h1>
      <p class="mt-2 text-gray-600">좌석 예약이 가능한 장소를 등록하세요</p>
    </div>

    <div class="space-y-8">
      <!-- 기본 정보 -->
      <div class="bg-white rounded-lg border shadow-sm p-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">기본 정보</h2>
        
        <div class="space-y-6">
          <!-- 제목 -->
          <div>
            <label for="title" class="form-label">제목 *</label>
            <input
              id="title"
              v-model="form.title"
              type="text"
              required
              class="form-input"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.title }"
              placeholder="예) 강남역 스터디 카페 좌석 예약"
            />
            <p v-if="errors.title" class="form-error">{{ errors.title }}</p>
          </div>

          <!-- 장소명 -->
          <div>
            <label for="place" class="form-label">장소명 및 주소 검색 <span class="text-xs form-error" style="font-size: smaller"> *검색을 통해 목록에서 선택하면 자동으로 입력됩니다 (권장)</span></label>
            <KakaoMap @select="selectPlace"/>
          </div>
          <div>
            <label for="place" class="form-label">장소명 *</label>
            <input
              id="place"
              v-model="form.place"
              type="text"
              required
              class="form-input"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.place }"
              placeholder="예) 강남 스터디 카페"
            />
            <p v-if="errors.place" class="form-error">{{ errors.place }}</p>
          </div>

          <!-- 주소 -->
          <div>
            <label for="address" class="form-label">주소 *</label>
            <input
              id="address"
              v-model="form.address"
              type="text"
              required
              class="form-input"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.address }"
              placeholder="예) 서울시 강남구 강남대로 123"
            />
            <p v-if="errors.address" class="form-error">{{ errors.address }}</p>
          </div>

          <!-- 이용 기간 -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="startDate" class="form-label">시작일 *</label>
              <input
                id="startDate"
                v-model="form.startDate"
                type="datetime-local"
                required
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.startDate }"
              />
              <p v-if="errors.startDate" class="form-error">{{ errors.startDate }}</p>
            </div>
            <div>
              <label for="endDate" class="form-label">종료일 *</label>
              <input
                id="endDate"
                v-model="form.endDate"
                type="datetime-local"
                required
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.endDate }"
              />
              <p v-if="errors.endDate" class="form-error">{{ errors.endDate }}</p>
            </div>
          </div>
          <div>
            <label for="maxSeatsPerUser" class="form-label">1인당 예약 가능 좌석 수 *</label>
            <input
              id="maxSeatsPerUser"
              v-model.number="form.maxSeatsPerUser"
              type="number"
              min="1"
              class="form-input"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.maxSeatsPerUser }"
              placeholder="예) 1"
            />
            <p v-if="errors.maxSeatsPerUser" class="form-error">{{ errors.maxSeatsPerUser }}</p>
          </div>
        </div>
      </div>

      <!-- 내용 -->
      <div class="bg-white rounded-lg border shadow-sm p-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">상세 설명</h2>
        
        <div>
          <label for="content" class="form-label">내용 *</label>
          <textarea
            id="content"
            v-model="form.content"
            required
            rows="8"
            class="form-input"
            :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.content }"
            placeholder="해당 장소 예약에 대한 자세한 설명을 작성해주세요.&#10;&#10;예)&#10;- 조용한 스터디 환경&#10;- Wi-Fi, 충전기 제공&#10;- 커피, 차 무료 제공&#10;- 주차 가능"
          ></textarea>
          <p v-if="errors.content" class="form-error">{{ errors.content }}</p>
        </div>
      </div>

      <!-- 좌석 설정 -->
      <div class="bg-white rounded-lg border shadow-sm p-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">좌석 설정 <span class="text-xs form-error">*좌석 배치의 크기를 먼저 설정하고 클릭으로 비활성화 시켜 배치도를 만들어 주세요.</span></h2>
        <div class="space-y-6">
          <!-- 좌석 수 설정 -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="rows" class="form-label">줄 수 *</label>
              <select
                id="rows"
                v-model="seatConfig.rows"
                class="form-input"
                @change="updateSeatLayout"
              >
                <option v-for="n in 12" :key="n" :value="n">{{ n }} 줄</option>
              </select>
            </div>
            <div>
              <label for="seatsPerRow" class="form-label">줄당 좌석 수 *</label>
              <select
                id="seatsPerRow"
                v-model="seatConfig.seatsPerRow"
                class="form-input"
                @change="updateSeatLayout"
              >
                <option v-for="n in 12" :key="n" :value="n">{{ n }} 개</option>
              </select>
            </div>
          </div>

          <!-- 좌석 배치 미리보기 -->
          <div>
            <label class="form-label">좌석 배치 미리보기</label>
            <div class="mt-2 p-4 bg-gray-50 rounded-lg">
              <SeatLayout :key="seatLayoutKey"
                          :mode="'Create'"
                          :layout="form.seatLayoutObj"
                          :readonly="false"
                          @handleSeatLayout="handleSeatLayout"
              />
            </div>
            <p class="form-hint mt-2">총 {{ form.seatLayoutObj.totalSeats }}개의 좌석이 생성됩니다.</p>
          </div>
        </div>
      </div>

      <!-- 에러 메시지 -->
      <div v-if="errors.general" class="bg-red-50 border border-red-200 rounded-md p-4">
        <p class="text-red-600 text-sm">{{ errors.general }}</p>
      </div>

      <!-- 액션 버튼 -->
      <div class="flex justify-end space-x-4">
        <RouterLink to="/posts" class="btn btn-outline btn-sm">
          취소
        </RouterLink>
        <button
          v-on:click="handleSubmit"
          :disabled="isSubmitting"
          class="btn btn-primary btn-sm"
          :class="{ 'opacity-50 cursor-not-allowed': isSubmitting }"
        >
          <span v-if="isSubmitting" class="flex items-center">
            <div class="spinner w-4 h-4 mr-2"></div>
            작성 중...
          </span>
          <span v-else>게시글 작성</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { postApi } from '@/services/api'
import { PostRequest } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import SeatLayout from '@/components/SeatLayout.vue'
import { serializeSeatStatus } from "@/utils";
import KakaoMap from "@/components/KakaoMap.vue";

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const isSubmitting = ref(false)
const seatLayoutKey = ref(0)
// 좌석 설정
const seatConfig = reactive({
  rows: 5,
  seatsPerRow: 8
})

// 폼 데이터
const form = reactive<PostRequest>({
  title: '',
  content: '',
  place: '',
  address: '',
  seatLayout: '',
  seatLayoutObj: {
    rows: 0,
    seatsPerRow: 0,
    totalSeats: 0,
    seatStatus: [[]]
  },
  startDate: '',
  endDate: '',
  maxSeatsPerUser: 1
})

// 에러 상태
const errors = reactive({
  title: '',
  content: '',
  place: '',
  address: '',
  startDate: '',
  endDate: '',
  maxSeatsPerUser: '',
  general: ''
})

// 총 좌석 수
const totalSeats = computed(() => seatConfig.rows * seatConfig.seatsPerRow)

// 좌석 레이아웃 업데이트
const updateSeatLayout = () => {
  const rows = seatConfig.rows
  const seatsPerRow = seatConfig.seatsPerRow

  const seatStatus = Array.from({ length: rows }, () =>
      Array.from({ length: seatsPerRow }, () => 1)
  )
  form.seatLayoutObj = {
    rows,
    seatsPerRow,
    totalSeats: totalSeats.value,
    seatStatus
  }
  // 컴포넌트 변경 적용하기 위한 키
  seatLayoutKey.value++
}

const handleSeatLayout = (seatLayout: PostRequest['seatLayoutObj']) => {
  form.seatLayoutObj = seatLayout
  form.seatLayout = serializeSeatStatus(seatLayout.seatStatus)
}

// 에러 초기화
const clearErrors = () => {
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })
}

// 카카오맵 위치 선택 이벤트
const selectPlace = (place: any) => {
  form.place = place.place_name
  form.address = place.road_address_name || place.address_name
}

// 폼 유효성 검사
const validateForm = (): boolean => {
  clearErrors()
  let isValid = true

  // 제목 검증
  if (!form.title.trim()) {
    errors.title = '제목을 입력해주세요.'
    isValid = false
  } else if (form.title.length > 100) {
    errors.title = '제목은 100자 이하로 입력해주세요.'
    isValid = false
  }

  // 장소명 검증
  if (!form.place.trim()) {
    errors.place = '장소명을 입력해주세요.'
    isValid = false
  }

  // 주소 검증
  if (!form.address.trim()) {
    errors.address = '주소를 입력해주세요.'
    isValid = false
  }

  // 내용 검증
  if (!form.content.trim()) {
    errors.content = '내용을 입력해주세요.'
    isValid = false
  } else if (form.content.length < 10) {
    errors.content = '내용은 10자 이상 입력해주세요.'
    isValid = false
  }

  // 날짜 검증
  if (!form.startDate) {
    errors.startDate = '시작일을 선택해주세요.'
    isValid = false
  }

  if (!form.endDate) {
    errors.endDate = '종료일을 선택해주세요.'
    isValid = false
  }

  if (!form.maxSeatsPerUser || form.maxSeatsPerUser < 1) {
    errors.maxSeatsPerUser = '1명 이상 예약 가능하도록 설정해주세요.'
    isValid = false
  }

  if (form.startDate && form.endDate) {
    const startDate = new Date(form.startDate)
    const endDate = new Date(form.endDate)
    const now = new Date()

    if (startDate < now) {
      errors.startDate = '시작일은 현재 시간보다 이후여야 합니다.'
      isValid = false
    }

    if (endDate <= startDate) {
      errors.endDate = '종료일은 시작일보다 이후여야 합니다.'
      isValid = false
    }
  }

  return isValid
}

// 폼 제출
const handleSubmit = async () => {
  if (!validateForm()) return

  try {
    isSubmitting.value = true
    const response = await postApi.createPost(form)
    
    toast.success('게시글이 작성되었습니다!')
    router.push(`/posts/${response.data.id}`)
  } catch (error: any) {
    console.error('Failed to create post:', error)
    if (error.response?.status === 400) {
      errors.general = '입력 정보를 확인해주세요.'
    } else {
      errors.general = '게시글 작성에 실패했습니다. 다시 시도해주세요.'
    }
    toast.error('게시글 작성에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

// 초기 설정
const initializeForm = () => {
  // 기본 날짜 설정 (현재시각 + 1시간)
  const now = new Date()
  const startDate = new Date(now.getTime() + 60 * 60 * 1000) // 1시간 후
  const endDate = new Date(now.getTime() + 24 * 60 * 60 * 1000) // 1일 후
  
  form.startDate = startDate.toISOString().slice(0, 16)
  form.endDate = endDate.toISOString().slice(0, 16)
  
  // 초기 좌석 레이아웃 설정
  updateSeatLayout()
}

onMounted(() => {
  initializeForm()
})

</script>
